import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, Box, Button, Drawer, screenSizeSmaller} from 'views/components';
import {Menu} from 'views/icons';

import FormPreview from './form_preview/FormPreview';
import ChatWindow from './chat_window/ChatWindow';
import Guide from './instruction_tour/Guide';

import backend from 'utils/backend';

const ClientProfile = (props) => {
  const progressWrap = props.statusProgressWrap;
  const clientRn = props.clientRn;
  const userRn = props.userRn;

  const [messages, setMessages] = React.useState([]);
  const [infoBoxData, setInfoBoxData] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [isTyping, setIsTyping] = React.useState(false);
  const [sessionId] = React.useState(() => {
    // Generate 6 random bytes (48 bits) for 12-character hex string
    const randomBytes = new Uint8Array(6);
    crypto.getRandomValues(randomBytes);
    const hexString = Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${userRn.toString()}:CHAT-V1-${hexString}`;
  });
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const wsRef = React.useRef(null);

  const isMobile = screenSizeSmaller('md');

  // Loading webhook client
  React.useEffect(() => {
    const onMessage = (data) => {
      if (data.message === 'Endpoint request timed out') {
        console.warn('Timeout message received, not adding to messages');
        return;
      }

      const botResponse = data.response;

      setMessages((prev) => [...prev, {author: 'bot', text: botResponse}]);
      setIsLoading(false);
      setIsTyping(false);
      updateInformationBoxes();
    };
    const onOpen = () => {
      wsRef.current.send({
        action: 'sendMessage',
        message: 'hello',
        sessionId,
        clientRn,
      });

      setIsTyping(true);
    };

    const onError = (error) => {
      console.error('WebSocket error:', error);
    };
    const onClose = () => {
    };

    wsRef.current = new backend.ClientOnboardingChat.OnboardingWebsocketManager(onMessage, onOpen, onError, onClose);

    wsRef.current.connect();

    return () => {
      wsRef.current?.close();
    };
  }, [sessionId]);

  // Loading initial form
  React.useEffect(() => {
    updateInformationBoxes();
  }, []);

  const updateInformationBoxes = async () => {
    progressWrap(async () => {
      const data = await backend.ClientOnboardingChat.fetchFormData(clientRn);
      await setInfoBoxData(data.form || {});
    }, {debugString: 'CLINET_PROFILE_GET_RESPONSE', showProgress: false});
  };

  const sendMessage = (input) => {
    if (!input.trim()) return;

    progressWrap(async () => {
      const newMessage = {author: 'user', text: input};
      setMessages((prev) => [...prev, newMessage]);

      wsRef.current?.send({action: 'sendMessage', message: input, sessionId, clientRn});

      setIsTyping(true);
    }, {debugString: 'CLIENT_PROFILE_SEND_MESSAGE', showProgress: false});
  };

  const manuallyUpdateField = (values) => {
    return progressWrap(async () => {
      const update = await backend.ClientOnboardingChat.updateParameter(clientRn, values);
      setInfoBoxData(update.form);
    }, {debugString: 'CLIENT_PROFILE_MANUAL_UPDATE', showProgress: false});
  };


  return (
    <>
      <Guide
        setDrawerOpen={setDrawerOpen}
      />

      <Box sx={{display: 'flex', flexDirection: 'column', height: '95vh', width: '100 vw', border: '1px solid #ccc', borderRadius: '12px', overflow: 'hidden'}}>
        <Box sx={{flexGrow: 1, overflow: 'hidden'}}>
          <Grid container sx={{height: '100%'}}>
            {/* Chat window */}
            <Grid
              id="chat-window"
              size="grow"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                p: 0,
                borderRadius: 'inherit',
                width: isMobile ? '100%' : undefined,
                position: isMobile ? 'relative' : undefined,
              }}
            >
              <ChatWindow
                messages={messages}
                sendMessage={sendMessage}
                isLoading={isLoading}
                isTyping={isTyping}
              />
              {
                isMobile && !drawerOpen && (
                  <Box id="mobile-button" sx={{position: 'absolute', top: 12, right: 12, zIndex: 1300}}>
                    <Button
                      onClick={() => setDrawerOpen(true)}
                      sx={{
                        background: '#3060d9',
                        color: '#fff',
                        minWidth: 0,
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                        p: 0,
                      }}
                    >
                      <Menu fontSize="medium" />
                    </Button>
                  </Box>
                )
              }
            </Grid>

            {/* Form preview */}
            {
              !isMobile && (
                <Grid id="form-preview" sx={{width: '45%', borderLeft: '1px solid #ccc', p: 2, height: '100%', overflow: 'hidden', position: 'relative'}}>
                  <Box sx={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto'}}>
                    <FormPreview
                      data={infoBoxData}
                      manuallyUpdateField={manuallyUpdateField}
                    />
                  </Box>
                </Grid>
              )
            }
          </Grid>
        </Box>

        {/* Mobile Drawer for FormPreview */}
        {
          isMobile && (
            <Drawer
              disableBackdropTransition={false}
              disableDiscovery={true}
              anchor="right"
              open={drawerOpen}
              onOpen={() => setDrawerOpen(true)}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{sx: {width: '90vw', maxWidth: 400, padding: 0}}}
              swipeAreaWidth={224}
              disableSwipeToOpen={false}
              keepMounted
              swipeable
            >
              <Box id="mobile-form-preview" sx={{height: '100%', overflow: 'auto'}}>
                <FormPreview
                  data={infoBoxData}
                  manuallyUpdateField={manuallyUpdateField}
                />
              </Box>
            </Drawer>
          )
        }
      </Box>
    </>
  );
};

ClientProfile.propTypes = {
  clientRn: PropTypes.string.isRequired,
  userRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  clientRn: state.user.clientRn,
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientProfile));
